/**
 * Circular player headshot. The MLB midfield CDN sometimes serves a
 * generic silhouette image (HTTP 200) for newly-added players whose
 * /spots/120 cache was generated before their photo went live — and
 * a separate /spots/240 cache, generated later, has the real photo.
 * To avoid the silhouette-on-lineup / photo-on-modal mismatch, every
 * surface fetches `/spots/240` (cache-shared). Display size is decoupled
 * via `displaySize`; 240×240 px decoded once, rendered as 32–88 px is
 * negligible memory at the ~30 unique players per game scale.
 *
 * Initials fallback sits BEHIND the <img> via z-index: -1 so a broken
 * image (rare — only true 404s) reveals the initials. `errored` is reset
 * when `personId` or `size` changes so a prior failure doesn't suppress
 * a later valid load in the same component instance.
 */
import { useEffect, useState } from 'react';
import { headshotUrl, type HeadshotSize } from '../utils/headshot';
import { getInitials } from '../utils/initials';
import styles from './PlayerAvatar.module.css';

interface PlayerAvatarProps {
  personId: number | null | undefined;
  name?: string | null;
  /** Pixel size for both the rendered box and the headshot fetch resolution. */
  size?: HeadshotSize;
  /** Optional separate display size — useful for small inline circles backed by a higher-res fetch. */
  displaySize?: number;
  className?: string;
}

export default function PlayerAvatar({
  personId,
  name,
  size = 240,
  displaySize,
  className,
}: PlayerAvatarProps): JSX.Element {
  const [errored, setErrored] = useState(false);
  // Reset the error state when the player or size changes so a prior 404
  // doesn't suppress a later valid render in the same component instance
  // (Codex Pass-2 finding).
  useEffect(() => {
    setErrored(false);
  }, [personId, size]);

  const url = headshotUrl(personId, size);
  const initials = name ? getInitials(name) : '?';
  const px = displaySize ?? size;

  const showImage = url && !errored;

  return (
    <span
      className={`${styles.avatar} ${className ?? ''}`}
      style={{ width: px, height: px }}
      data-testid="player-avatar"
      aria-label={name ?? `Player ${personId ?? 'unknown'}`}
    >
      {showImage && (
        <img
          src={url}
          alt=""
          width={px}
          height={px}
          decoding="async"
          onError={() => setErrored(true)}
          className={styles.img}
        />
      )}
      {/* Initials sit BEHIND the image (z-index: -1). They're visible only
       * when the image fails to load (rare). No flash during normal load. */}
      <span className={`${styles.fallback} ${showImage ? styles.fallbackHidden : ''}`} aria-hidden="true">
        {initials}
      </span>
    </span>
  );
}
